import { useState } from 'react';
import styled from 'styled-components';
import { DataTable } from 'primereact/datatable';
import { DateTime } from 'luxon';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { ProgressBar } from 'primereact/progressbar';
import { TableWrapper, ValueUpdateInfo } from './common';
import { ActionsWrapper } from '../DataTable/common';
import { getReasonStrById } from './common';

const Wrapper = styled.div``;

const Subtitle = styled.p`
  font-weight: bold;
  text-decoration: underline;
`;

const DetailWrapper = styled.div`
  margin: 16px 0;
  padding: 16px 8px;
  background-color: #edeff5;
`;

function PendingChangesModal({
  isOpen,
  loading,
  data,
  canApproveTarget,
  reasonsData,
  onApprove,
  onReject,
  onCancel,
}: {
  isOpen: boolean;
  loading: boolean;
  data: any;
  canApproveTarget: boolean;
  reasonsData: any[];
  onApprove: (submissionId: number) => void;
  onReject: (submissionId: number) => void;
  onCancel: () => void;
}) {
  const [expandedRows, setExpandedRows] = useState([]);

  const dateTimeCellTemplate = (rowData: any) =>
    rowData.idt
      ? DateTime.fromISO(rowData.idt).toLocaleString({
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
        })
      : '-';

  const rowExpansionTemplate = (detailRowData: any) => (
    <DetailWrapper>
      <Subtitle>
        {detailRowData.type === 'division' ? 'Submitted divisional changes' : 'Submitted mill changes'}{' '}
      </Subtitle>
      <DataTable value={detailRowData.targetEntries} size="small">
        <Column field="id" header="Change ID" />
        <Column field="name" header="Target Name" body={(rowData: any) => `${rowData.name} (${rowData.code})`} />
        {detailRowData.type === 'division' && (
          <Column
            field="targetType"
            header="Type"
            body={(rowData: any) => (rowData.targetType === 'division' ? 'Mill annual' : 'Baseline')}
          />
        )}
        <Column field="" header="Metric Type" body={() => detailRowData.metricType || '-'} />
        <Column field="" header="Source" body={() => detailRowData.sourceName || '-'} />
        <Column field="year" header="Year" />
        <Column field="value" header="Target Value" />
        <Column field="oldValue" header="Old Target Value" />
        <Column field="reasonId" header="Reason" body={(rowData) => getReasonStrById(rowData?.reasonId, reasonsData)} />
      </DataTable>
    </DetailWrapper>
  );

  return (
    <Dialog
      visible={isOpen}
      style={{ minWidth: '600px', maxWidth: '800px' }}
      header="Pending changes waiting for approval"
      modal
      footer={
        canApproveTarget ? (
          <ActionsWrapper>
            <Button
              label="Reject"
              size="small"
              severity="warning"
              disabled={loading}
              onClick={() => onReject(data[0].id)}
            />
            <Button label="Approve" size="small" disabled={loading} onClick={() => onApprove(data[0].id)} />
            <Button label="Cancel" size="small" text onClick={onCancel} />
          </ActionsWrapper>
        ) : (
          <Button label="Close" icon="pi pi-times" size="small" text onClick={onCancel} />
        )
      }
      onHide={onCancel}
    >
      <Wrapper>
        {loading && <ProgressBar mode="indeterminate" style={{ height: '4px', marginBottom: '16px' }} />}
        {canApproveTarget ? (
          <div>
            <ValueUpdateInfo>
              <span>Submission ID:</span>
              <span>{data[0].id}</span>
              <span>Submitted By:</span>
              <span>{`${data[0].user.name} ${data[0].user.familyName}`}</span>
              <span>Submitted At:</span>
              <span>
                {DateTime.fromISO(data[0].idt).toLocaleString({
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                  second: 'numeric',
                })}
              </span>
            </ValueUpdateInfo>
            <TableWrapper>{rowExpansionTemplate(data[0])}</TableWrapper>
          </div>
        ) : (
          <TableWrapper>
            <DataTable
              value={data}
              size="small"
              tableStyle={{ minWidth: '50rem' }}
              expandedRows={expandedRows}
              onRowToggle={(e: any) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              dataKey="id"
            >
              <Column expander style={{ width: '20px' }} />
              <Column field="id" header="ID" />
              <Column
                field="type"
                header="Type"
                body={(rowData: any) => (rowData.type === 'division' ? 'Divisional' : 'Mill')}
              />
              <Column field="idt" header="Submitted At" body={dateTimeCellTemplate} />
            </DataTable>
          </TableWrapper>
        )}
      </Wrapper>
    </Dialog>
  );
}

export default PendingChangesModal;
